import type { Query, QueryClient } from '@tanstack/react-query'

import {
  isAssistantEffect,
  type AssistantEffect,
} from '@/lib/assistant-effects.generated'
import { getSessionEpoch, hydrateIdentity, resetIdentityForSwitch } from '@/lib/auth'
import { isUuid } from '@/lib/entity'
import { clearImpersonationTarget, setImpersonationTarget } from '@/lib/impersonation'

export function isSessionChange(result: unknown): boolean {
  if (typeof result !== 'object' || result === null) return false
  const value = result as Record<string, unknown>
  return typeof value.targetUserId === 'string' || value.impersonationStopped === true
}

export async function applySessionChange(
  result: unknown,
  queryClient: QueryClient,
  selectedEntityId?: string,
): Promise<number | null> {
  if (!isSessionChange(result)) return null
  const value = result as Record<string, unknown>
  if (typeof value.targetUserId === 'string') setImpersonationTarget(value.targetUserId)
  else clearImpersonationTarget()
  resetIdentityForSwitch()
  const sessionEpoch = getSessionEpoch()
  queryClient.clear()
  await hydrateIdentity(selectedEntityId)
  return sessionEpoch
}

export function executionResult(result: unknown) {
  if (typeof result !== 'object' || result === null)
    return { value: result, effects: [] as AssistantEffect[] }
  const execution = result as Record<string, unknown>
  return {
    value: execution.value,
    effects: Array.isArray(execution.effects)
      ? execution.effects.filter(isAssistantEffect)
      : [],
  }
}

export function reportingCreation(result: unknown) {
  if (typeof result !== 'object' || result === null || Array.isArray(result)) return null
  const value = result as Record<string, unknown>
  if (
    typeof value.reportDefinitionId !== 'string' || value.reportDefinitionId.length !== 36 || !isUuid(value.reportDefinitionId)
    || typeof value.entityId !== 'string' || value.entityId.length !== 36 || !isUuid(value.entityId)
    || typeof value.name !== 'string' || !value.name.trim()
  ) return null
  return {
    text: `Created the report "${value.name}".`,
    destination: {
      label: 'Open report',
      path: `/${value.entityId.toLowerCase()}/reports/${value.reportDefinitionId.toLowerCase()}`,
    },
  }
}

export async function applyAssistantEffects(
  effects: readonly AssistantEffect[],
  queryClient: QueryClient,
  sessionEpoch: number,
): Promise<void> {
  for (const effect of effects) {
    if (getSessionEpoch() !== sessionEpoch) return
    const filters = {
      predicate: (query: Query) =>
        query.meta?.assistantEffects?.includes(effect) === true,
    }
    await queryClient.cancelQueries(filters)
    if (getSessionEpoch() !== sessionEpoch) return
    await queryClient.invalidateQueries(filters)
  }
}
